// Informe de Paradas para la vista web. Es el MISMO cálculo que el archivo:
//   apps/torre-de-control-api/functions/src/modules/reports/reports/stopsReportRule.js
// Si cambias uno, cambia el otro: stopsReportRule.test.ts repite los casos de
// stopsReportRule.unit.test.js del backend.
import {
  cleanOdometer,
  estimateSpeeds,
  intervalDistances,
  resolveOdoKm,
  type IntervalPoint,
} from "../utils/intervalDistance";
import type { RawHistoryPoint } from "../utils/trackingHistoryWindows";

// Detenido = 3 km/h o menos (en marcha es > 3, como en idleRule).
export const STOP_SPEED_THRESHOLD = 3;

export type StopBucket = "0_5" | "5_15" | "15_30" | "30_60" | "60_mas";

export const classifyStop = (durationSeconds: number): StopBucket => {
  const minutes = durationSeconds / 60;
  if (minutes < 5) return "0_5";
  if (minutes < 15) return "5_15";
  if (minutes < 30) return "15_30";
  if (minutes < 60) return "30_60";
  return "60_mas";
};

const num = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
};

/** Punto crudo de /tracking/history al formato de la regla (lat/lon 0 = sin coordenadas, como el backend). */
export const toStopPoint = (p: RawHistoryPoint): IntervalPoint => ({
  ts: p.ts,
  lat: num(p.lat) ?? 0,
  lon: num(p.lng) ?? 0,
  speed: num(p.speed),
  odoKm: resolveOdoKm(p.params),
});

/**
 * Paradas de una unidad (puntos ordenados por tiempo). La velocidad vacía se
 * estima como en los Informes rápidos: en Ruptela llega null en 35-50% de los
 * puntos aun en marcha, y tomarla como 0 abría paradas falsas.
 */
export const buildStops = (
  points: IntervalPoint[],
): { buckets: Record<StopBucket, number>; totalParadas: number; tiempoTotalSeconds: number } => {
  const dist = intervalDistances(points, cleanOdometer(points), -Infinity).dist;
  const speeds = estimateSpeeds(points, dist);

  const buckets: Record<StopBucket, number> = { "0_5": 0, "5_15": 0, "15_30": 0, "30_60": 0, "60_mas": 0 };
  let totalParadas = 0;
  let tiempoTotalSeconds = 0;
  let stopStartMs: number | null = null;
  const close = (endMs: number) => {
    const dur = (endMs - (stopStartMs as number)) / 1000;
    buckets[classifyStop(dur)]++;
    totalParadas++;
    tiempoTotalSeconds += dur;
    stopStartMs = null;
  };
  for (let i = 0; i < points.length; i++) {
    const isStopped = speeds[i] <= STOP_SPEED_THRESHOLD;
    if (isStopped && stopStartMs === null) stopStartMs = points[i].ts;
    else if (!isStopped && stopStartMs !== null) close(points[i].ts);
  }
  if (stopStartMs !== null) close(points[points.length - 1].ts);
  return { buckets, totalParadas, tiempoTotalSeconds };
};
